import React, { useState } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, Form, Grid, Icon } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { useTranslation } from 'react-i18next'
import InfoPopup from '~/components/InfoPopup'
import { StyledGrid, StyledLabel } from './style'
import { CheckClimaEl } from '../../../style'
import LinkPopup from '../LinkPopup'
import { EnergyConsumption } from '~/api/services/co2'
import DieselPerCow from './DieselPerCow'
import EnergyPerCow from './EnergyPerCow'
import { CHILDREN_RENEWABLE_ENERGY_SLUGS, GREEN_MIX_SLUG, RENEWABLE_ENERGY_PROD_SLUG } from '../../constants'
import ErrorIcon from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ErrorIcon'

interface Props {
  energyConsumptions: EnergyConsumption[]
}

const EnergyFields: React.FC<Props> = ({ energyConsumptions }) => {
  const { t } = useTranslation()
  const { control, getValues, formState: { isSubmitted } } = useFormContext()

  const [hasBlurred, setHasBlurred] = useState(false)

  const { fields, append, replace } = useFieldArray({
    control,
    name: 'cowsEnergyConsumptions',
    keyName: 'key'
  })

  useWatch({ control, name: 'cowsEnergyConsumptions' })

  function handleChecked(field, checked) {
    if (checked) {
      append({
        ...field,
        percent: ''
      })
      return
    }
    const index = fields.findIndex(fieldEnergie => fieldEnergie['id'] === field.id)
    const newFields = index >= 0 ? fields.filter(field => field['id'] !== fields[index]['id']) : fields
    // remove children
    const filteredChildrenFields = index >= 0 ? newFields.filter(field => field['parentId'] !== fields[index]['id']) : newFields
    if (index >= 0) {
      replace(filteredChildrenFields)
    }
  }

  function checkPercentage() {
    const energyTypes = getValues('cowsEnergyConsumptions').filter(value => !CHILDREN_RENEWABLE_ENERGY_SLUGS.find(slug => slug === value.slug))
    const allFieldsEmpty = energyTypes.findIndex(energy => Boolean(energy?.percent)) < 0
    return !allFieldsEmpty && energyTypes.reduce((previousValue, currentValue) => {
      return previousValue + parseFloat(currentValue.percent.replace(',', '.'))
    }, 0) !== 100
  }

  function checkPercentageChild() {
    const childEnergyTypes = getValues('cowsEnergyConsumptions').filter(value => CHILDREN_RENEWABLE_ENERGY_SLUGS.find(slug => slug === value.slug))
    const allFieldsEmpty = childEnergyTypes?.length === 0
    return !allFieldsEmpty && childEnergyTypes.reduce((previousValue, currentValue) => {
      return previousValue + parseFloat(currentValue.percent.replace(',', '.'))
    }, 0) !== 100
  }

  function renderShareRenewableEnergie() {
    return getValues('cowsEnergyConsumptions').reduce((previousValue, currentValue) => {
      const updateValue = currentValue['slug'] === RENEWABLE_ENERGY_PROD_SLUG || currentValue['slug'] === GREEN_MIX_SLUG ?
        parseFloat(currentValue['percent']?.replace(',', '.') || 0) : 0
      return previousValue + updateValue
    }, 0)
  }

  function renderEnergyConsumption() {

    return <Form.Group widths={'equal'} style={{ gap: 20 }}>
      <Form.Field>
        <Form.Group widths={'equal'}>
          <FormFields.Input
            control={control}
            typeInput='controled'
            rules={{
              min: 0,
              max: 100000,
              required: true
            }}
            suffix={' Liter pro Jahr'}
            noErrorMessage
            label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <label>{t('Diesel consumption (l/Kuh u. Jahr)')}</label>
              <InfoPopup
                content={`Hier zählt der direkte Verbrauch für die Fütterung (Mischen der Kuhfutterration) sowie für das Misten/Einstreuen. 
                  Außenwirtschaftliche Tätigkeiten wie Häckseln und Düngen gehören nicht dazu. Der Dieselverbrauch für die Färsen wird separat abgefragt. 
                  Der durchschnittliche Dieselverbrauch liegt in etwa bei 30 Liter je Kuh und Jahr. `}
                styleIcon={{ fontSize: '1.2em' }}
                position={'top center'}
              />
            </div>}
            withoutAsterisk
            name={'dieselConsumption'}
            placeholder={'Liter pro Jahr'}
          />
          <DieselPerCow />
        </Form.Group>
      </Form.Field>

      <Form.Field>
        <Form.Group widths={'equal'}>
          <FormFields.Input
            control={control}
            name={'energyUse'}
            suffix={' kWh'}
            withoutAsterisk
            label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <label>{'Energieeinsatz/Stromverbrauch'}</label>
              <InfoPopup
                content={`Wie viel Strom haben Sie im Milchkuhstall verbraucht? Hier sind Melken, Kühlen, Lampen, etc. gemeint. 
                Als Faustzahl gibt das KTBL eine Spanne von 23.400 bis 40.900 kWh/Jahr für einen Betrieb mit 100 Kühen ohne AMS und Weidegang an. Der Stromverbrauch in der Färsenaufzucht wird separat abgefragt.`}
                position={'top center'}
                styleIcon={{ fontSize: '1.2em' }}
              />

              <LinkPopup
                hoverable={true}
                removePrefix
                content={`<div style='display: flex; flex-direction: column; white-space: nowrap;'>
                <div style='white-space: nowrap;'>Datenquelle: Stromabrechnung, bei Eigenstromerzeugung Stromzählerstände</div>
                <a href='https://www.ktbl.de/webanwendungen/energiebedarfsrechner' target='_blank' style='color: white; white-space: nowrap; text-decoration: none;'>Kalkulationshilfe:
                 <span style='text-decoration: underline;'>https://www.ktbl.de/webanwendungen/energiebedarfsrechner</span>
                 </a>
              </div>`}
              />
              <ErrorIcon
                control={control}
                name='energyUse'
              />
            </div>}
            typeInput='controled'
            noErrorMessage
            rules={{
              required: true,
              min: {
                value: 10,
                message: 'Der Stromverbrauch muss mindestens 10 kWh betragen.'
              }
            }}
            placeholder={'kWh pro Jahr'}
          />
          <EnergyPerCow />
        </Form.Group>
      </Form.Field>

    </Form.Group>
  }

  function renderEnergyInput(option, error) {
    const fieldIndex = fields.findIndex(field => field['id'] === option.id)
    const checkedEnergy = fieldIndex >= 0

    return <>
      <Grid.Row
        key={option.id}>
        <Checkbox
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{option.label}</div>
            {option?.info && <InfoPopup
              content={option.info}
              position={'top center'}
              styleIcon={{ fontSize: '1.2em' }}
            />}
            {option?.dataSource && <LinkPopup
              content={option?.dataSource?.replace('Datenquelle: ', '')}
            />}
          </label>}
          disabled={false}
          checked={Boolean(fields.find(energyField => energyField['id'] === option.id))}
          onChange={(e, data) => handleChecked(option, data.checked)}
          style={{ marginBottom: '0.5em' }}
        />
        {checkedEnergy ? <div onBlur={() => setHasBlurred(true)}><FormFields.Input
          control={control}
          styleLabel={{ marginBottom: 0 }}
          key={fields[fieldIndex]?.key}
          error={(isSubmitted || hasBlurred) && error}
          typeInput={'controled'}
          suffix={' %'}
          noErrorMessage
          withoutAsterisk
          rules={{
            required: true
          }}
          styleControlled={{ pointerEvents: checkedEnergy ? 'all' : 'none', opacity: checkedEnergy ? 1 : '0.6' }}
          name={`cowsEnergyConsumptions[${fieldIndex}].percent`}
          placeholder={t('in %')}
        /></div> : <Form.Input error={checkedEnergy && isSubmitted && error && Boolean(option.parentId)}
          style={{ pointerEvents: checkedEnergy ? 'all' : 'none', opacity: checkedEnergy ? 1 : '0.6' }}
          placeholder={t('in %')} />}
      </Grid.Row>
      {option.children?.length > 0 && checkedEnergy && <div style={{ fontStyle: 'italic' }}>{'Setzt sich zusammen aus:'}</div>}
      {option.children?.length > 0 && checkedEnergy && <div style={{ padding: '1em', border: '1px solid rgba(34,36,38,.15)', borderRadius: '.28571429rem' }}>
        <div>
          {option.children.map((option) => {
            return renderEnergyInput(option, checkPercentageChild())
          })}
        </div>
      </div>
      }
    </>
  }

  function handleFormValidate() {
    if (fields.find(energy => energy['slug'] === RENEWABLE_ENERGY_PROD_SLUG)) {
      return !checkPercentage() && !checkPercentageChild()
    }
    return !checkPercentage()
  }

  return <Form.Field>
    <label style={{ fontSize: '15px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div>{'Energieeinsatz'}</div>
        <InfoPopup
          content={t('Please select all the types from which you receive your electricity. Then distribute the percentage share among the selected options. In total these must add up to 100%.')}
          styleIcon={{ fontSize: '1.2em' }}
          position={'top center'}
        />
      </div>
    </label>
    <CheckClimaEl.StyledDivider />
    <div style={{ width: 'fit-content', marginBottom: '2em', marginTop: '1em' }}>
      <div>{'Kraftstoff- und Energieverbrauch '}</div>
      <CheckClimaEl.StyledDivider />
    </div>
    {renderEnergyConsumption()}
    <StyledGrid columns={4} style={{ marginTop: '1em' }}>
      <Controller
        name={'cowsEnergyConsumptions'}
        control={control}
        render={({ fieldState }) => {
          const error = handleFormValidate()
          return <>
            {energyConsumptions.map((option, index) => {
              return <Grid.Column key={`energyCow-${index}`}>
                {renderEnergyInput(option, !error)}
              </Grid.Column>
            })}
            <Grid.Column>
              <Form.Field
                style={{
                  padding: 9,
                  borderRadius: 5,
                  marginTop: 15,
                  display: 'flex',
                  gap: 5,
                  alignItems: 'center'
                }}>
                <Icon name='leaf' size='big' color='green' />
                <div>
                  <StyledLabel>
                    <div>{'Anteil Erneuerbare Energie'}</div>
                  </StyledLabel>
                  <div className='percent'>{`${renderShareRenewableEnergie()} %`}</div>
                </div>
              </Form.Field>
            </Grid.Column>
          </>
        }}
      />
    </StyledGrid>
  </Form.Field>
}
export default EnergyFields